import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Swal from 'sweetalert2';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { API_ENDPOINTS, API_HEADERS, API_METHODS } from '../services/constants';
import '../styles/Payment.css';

interface Borc {
  COUNTER: number;
  TARIH: string;
  ACIKLAMA: string;
  BORC: number;
  ALACAK: number;
  uyeid: number;
}

interface Grup {
  uyeid: number;
  ADI: string;
  SOYADI: string;
  GRUBU: string;
  DURUMU: string;
  KARTRESIM?: string;
  UYENO: number;
  toplam_borc: number;
  borclar: Borc[];
}

interface SepetItem {
  OdemeId: string;
  Tutar: number;
  Aciklama: string;
  AdSoyad: string;
  Tarih: string;
}

const PaymentSimple: React.FC = () => {
  const navigate = useNavigate();
  const [gruplar, setGruplar] = useState<Grup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Sepet state'leri
  const [sepetItems, setSepetItems] = useState<SepetItem[]>([]);
  const [secilenBorclar, setSecilenBorclar] = useState<Set<string>>(new Set());
  const [toplamTutar, setToplamTutar] = useState(0);
  const [sepetId, setSepetId] = useState<string>('');
  
  // UI state'leri
  const [activeStep, setActiveStep] = useState<'debts' | 'payment'>('debts');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Kart bilgileri
  const [kartBilgileri, setKartBilgileri] = useState({
    cardName: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvv: ''
  });

  useEffect(() => {
    // Native platform kontrolü
    if (Capacitor.isNativePlatform()) {
      console.log('Native platformda Payment sayfası açılıyor...');
      const tempUser = {
        uyeid: '123',
        isim: 'Test User',
        telefon: '5551234567'
      };
      localStorage.setItem('userInfo', JSON.stringify(tempUser));
      localStorage.setItem('isAuthenticated', 'true');
    }

    const userInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
    const isAuth = localStorage.getItem('isAuthenticated') === 'true';
    
    if (!userInfo || !isAuth) {
      navigate('/login');
      return;
    }

    const user = JSON.parse(userInfo || '{}');
    
    // Mevcut sepet ID'yi localStorage'dan al veya yeni oluştur
    let currentSepetId = localStorage.getItem('currentSepetId');
    if (!currentSepetId) {
      currentSepetId = `sepet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('currentSepetId', currentSepetId);
    }
    setSepetId(currentSepetId);
    
    fetchBorclar(user.uyeid || '123', user.uyeno || '123', user.kimlikno || '123', user.grubu || 'A', currentSepetId);
  }, [navigate]);

  // Gruplar yüklendikten sonra sepeti yükle
  useEffect(() => {
    if (gruplar.length > 0 && sepetId) {
      loadExistingSepet(sepetId);
    }
  }, [gruplar, sepetId]);

  const fetchBorclar = async (uyeId: string, uyeno: string, kimlikno: string, grubu: string, currentSepetId: string) => {
    try {
      console.log('Payment - Loading başlıyor...');
      setLoading(true);
      
      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          method: API_METHODS.GET_BORCLAR,
          uyeId: uyeId,
          uyeno: uyeno,
          kimlikno: kimlikno,
          grubu: grubu
        })
      });

      const data = await response.json();
      console.log('Payment - Gelen veri:', data);
      
      if (data.success) {
        console.log('Payment - Borçlar:', data.data.borclar);
        setGruplar(data.data.borclar || []);
        setError(null);
      } else {
        console.error('Payment - Hata:', data.message);
        setError(data.message || 'Borçlar yüklenirken hata oluştu');
      }
    } catch (err) {
      console.error('Borç getirme hatası:', err);
      setError('Borçlar yüklenirken bir hata oluştu');
    } finally {
      console.log('Payment - Loading bitiyor...');
      setLoading(false);
    }
  };

  // Mevcut sepeti yükle
  const loadExistingSepet = async (currentSepetId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          method: API_METHODS.GET_SEPET,
          SepetId: currentSepetId
        })
      });

      const data = await response.json();
      
      if (data.success && data.data) {
        setSepetItems(data.data);
        const secilenSet = new Set<string>();
        let toplam = 0;
        
        data.data.forEach((item: SepetItem) => {
          secilenSet.add(item.OdemeId.toString());
          toplam += parseFloat(item.Tutar.toString());
        });
        
        console.log('Sepetten yüklenen seçili borçlar (string):', Array.from(secilenSet));
        setSecilenBorclar(secilenSet);
        setToplamTutar(toplam);
      }
    } catch (error) {
      console.error('Sepet yükleme hatası:', error);
    }
  };

  // Sepete borç ekle
  const addToCart = async (borcId: string, amount: number, description: string, uyeid: number, uye: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          method: API_METHODS.CREATE_SEPET,
          borcid: borcId,
          amount: amount,
          description: description,
          uyeid: uyeid,
          uye: uye,
          SepetId: sepetId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSecilenBorclar(prev => new Set(prev).add(borcId));
        setToplamTutar(prev => prev + amount);
        loadExistingSepet(sepetId);
        
        // SweetAlert bildirimi
        Swal.fire({
          icon: 'success',
          title: 'Sepete Eklendi!',
          text: `${description} - ${formatCurrency(amount)}`,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });
        
        console.log('Borç sepete eklendi:', description);
      } else {
        throw new Error(data.message || 'Borç eklenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Sepete ekleme hatası:', error);
      Swal.fire({
        icon: 'error',
        title: 'Hata!',
        text: 'Borç sepete eklenirken bir hata oluştu: ' + (error as Error).message,
        confirmButtonColor: '#dc2626'
      });
    }
  };

  // Sepetten borç çıkar
  const removeFromCart = async (borcId: string, amount: number, description?: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          method: API_METHODS.REMOVE_SEPET,
          borcid: borcId,
          SepetId: sepetId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSecilenBorclar(prev => {
          const newSet = new Set(prev);
          newSet.delete(borcId);
          return newSet;
        });
        setToplamTutar(prev => prev - amount);
        loadExistingSepet(sepetId);
        
        // SweetAlert bildirimi
        Swal.fire({
          icon: 'info',
          title: 'Sepetten Çıkarıldı',
          text: `${description || 'Borç'} - ${formatCurrency(amount)}`,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });
        
        console.log('Borç sepetten çıkarıldı:', borcId);
      } else {
        throw new Error(data.message || 'Borç çıkarılırken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Sepetten çıkarma hatası:', error);
      Swal.fire({
        icon: 'error',
        title: 'Hata!',
        text: 'Borç sepetten çıkarılırken bir hata oluştu: ' + (error as Error).message,
        confirmButtonColor: '#dc2626'
      });
    }
  };

  // Borç seçimi değiştiğinde
  const handleBorcSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    borc: Borc,
    grup: Grup
  ) => {
    const borcId = borc.COUNTER.toString();
    const borcTutar = (borc.BORC || 0) - (borc.ALACAK || 0);
    const uye = `${grup.ADI} ${grup.SOYADI}`;

    if (e.target.checked) {
      addToCart(borcId, borcTutar, borc.ACIKLAMA, grup.uyeid, uye);
    } else {
      removeFromCart(borcId, borcTutar, borc.ACIKLAMA);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const turkceAy = (date: string) => {
    const months: { [key: string]: string } = {
      'January': 'Ocak', 'February': 'Şubat', 'March': 'Mart', 'April': 'Nisan',
      'May': 'Mayıs', 'June': 'Haziran', 'July': 'Temmuz', 'August': 'Ağustos',
      'September': 'Eylül', 'October': 'Ekim', 'November': 'Kasım', 'December': 'Aralık'
    };
    
    const dateObj = new Date(date);
    const month = dateObj.toLocaleString('en-US', { month: 'long' });
    const year = dateObj.getFullYear();
    
    return `${months[month]} ${year}`;
  };

  // Ödeme adımına geç
  const goToPayment = () => {
    if (secilenBorclar.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Uyarı',
        text: 'Lütfen ödeme yapmak istediğiniz borçları seçin.',
        confirmButtonColor: '#2563eb'
      });
      return;
    }
    setActiveStep('payment');
  };

  // Sepeti temizle
  const clearCart = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Sepeti Temizle',
      text: 'Sepeti temizlemek istediğinizden emin misiniz?',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Evet, Temizle',
      cancelButtonText: 'İptal'
    });

    if (result.isConfirmed) {
      try {
        // Tüm seçili borçları sepetten çıkar
        for (const borcId of Array.from(secilenBorclar)) {
          await fetch(API_ENDPOINTS.WEBSERVICE, {
            method: 'POST',
            headers: API_HEADERS,
            body: JSON.stringify({
              method: API_METHODS.REMOVE_SEPET,
              borcid: borcId,
              SepetId: sepetId
            })
          });
        }
        
        // State'leri temizle
        setSecilenBorclar(new Set());
        setSepetItems([]);
        setToplamTutar(0);
        
        // Yeni sepet ID oluştur
        const newSepetId = `sepet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setSepetId(newSepetId);
        localStorage.setItem('currentSepetId', newSepetId);
        
        Swal.fire({
          icon: 'success',
          title: 'Sepet Temizlendi!',
          text: 'Tüm öğeler sepetten çıkarıldı.',
          timer: 1500,
          showConfirmButton: false
        });
        
      } catch (error) {
        console.error('Sepet temizleme hatası:', error);
        Swal.fire({
          icon: 'error',
          title: 'Hata!',
          text: 'Sepet temizlenirken bir hata oluştu.',
          confirmButtonColor: '#dc2626'
        });
      }
    }
  };

  // Ödeme formu submit
  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    if (secilenBorclar.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Uyarı',
        text: 'Lütfen ödeme yapmak istediğiniz borçları seçin.',
        confirmButtonColor: '#2563eb'
      });
      setIsProcessing(false);
      return;
    }

    if (!kartBilgileri.cardName || !kartBilgileri.cardNumber || !kartBilgileri.cardExpiry || !kartBilgileri.cardCvv) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik Bilgi',
        text: 'Lütfen tüm kart bilgilerini doldurun.',
        confirmButtonColor: '#2563eb'
      });
      setIsProcessing(false);
      return;
    }

    try {
      // Simülasyon için 2 saniye bekle
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Başarılı ödeme sonrası
      await Swal.fire({
        icon: 'success',
        title: 'Ödeme Başarılı!',
        text: `${formatCurrency(toplamTutar)} tutarında ödeme başarıyla tamamlandı.`,
        confirmButtonColor: '#22c55e',
        confirmButtonText: 'Tamam'
      });
      
      // Sepeti temizle
      setSecilenBorclar(new Set());
      setSepetItems([]);
      setToplamTutar(0);
      
      // Yeni sepet ID oluştur
      const newSepetId = `sepet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSepetId(newSepetId);
      localStorage.setItem('currentSepetId', newSepetId);
      
      // Borçlar sayfasına dön
      setActiveStep('debts');
      
    } catch (error) {
      console.error('Ödeme hatası:', error);
      Swal.fire({
        icon: 'error',
        title: 'Ödeme Hatası',
        text: 'Ödeme işlemi sırasında bir hata oluştu.',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Header title="Borç Ödeme" />
        <div className="page-content">
          <div className="modern-loading">
            <div className="loading-spinner"></div>
            <p>Borçlar yükleniyor...</p>
          </div>
        </div>
        <TabBar />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <Header title="Borç Ödeme" />
        <div className="page-content">
          <div className="modern-error">
            <div className="error-icon">⚠️</div>
            <h3>Bir Hata Oluştu</h3>
            <p>{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              Tekrar Dene
            </button>
          </div>
        </div>
        <TabBar />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Header title="Borç Ödeme" />
      <div className="page-content">
        <div className="modern-payment-container">
          
          {/* Progress Steps */}
          <div className="payment-steps">
            <div className={`step ${activeStep === 'debts' ? 'active' : 'completed'}`}>
              <div className="step-number">1</div>
              <span>Borç Seçimi</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${activeStep === 'payment' ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <span>Ödeme</span>
            </div>
          </div>

          {/* Borç Seçimi Adımı */}
          {activeStep === 'debts' && (
            <div className="step-content">
              <div className="debts-layout">
                
                {/* Sol: Borç Listesi */}
                <div className="debts-section">
                  <div className="section-card">
                    <div className="section-header">
                      <h3>💰 Borç Listesi</h3>
                      <span className="debt-count">{gruplar.length} kişi</span>
                    </div>
                    
                    <div className="debts-content">
                      {gruplar.length === 0 ? (
                        <div className="no-debts">
                          <div className="no-debts-icon">🎉</div>
                          <h4>Harika!</h4>
                          <p>Ödenecek borç bulunamadı.</p>
                        </div>
                      ) : (
                        <div className="debt-cards">
                          {gruplar.map((grup, grupIndex) => (
                            <div key={grupIndex} className="debt-user-card">
                              <div className="user-header">
                                <div className="user-info">
                                  {grup.KARTRESIM ? (
                                    <img 
                                      src={`https://crm.havalimanitaksi.com.tr/media/natural/${grup.KARTRESIM}`}
                                      alt={`${grup.ADI} ${grup.SOYADI}`}
                                      className="user-avatar"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/images/user-placeholder.png';
                                      }}
                                    />
                                  ) : (
                                    <div className="user-avatar-placeholder">
                                      {grup.ADI.charAt(0)}{grup.SOYADI.charAt(0)}
                                    </div>
                                  )}
                                  <div className="user-details">
                                    <h4>{grup.ADI} {grup.SOYADI}</h4>
                                    <span className="user-role">{grup.GRUBU}</span>
                                    {grup.toplam_borc > 0 && (
                                      <span className="total-debt">
                                        {formatCurrency(grup.toplam_borc)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {grup.borclar && grup.borclar.length > 0 && (
                                <div className="debt-items">
                                  {grup.borclar.map((borc, borcIndex) => {
                                    const borcTutar = (borc.BORC || 0) - (borc.ALACAK || 0);
                                    if (borcTutar <= 0) return null;
                                    
                                    const borcId = borc.COUNTER.toString();
                                    const isSelected = secilenBorclar.has(borcId);
                                    
                                    // Debug: Seçim kontrolü
                                    if (secilenBorclar.size > 0) {
                                      console.log(`Borç ID: "${borcId}" (${typeof borcId}), Seçili mi: ${isSelected}, Sepetteki IDs: [${Array.from(secilenBorclar).map(id => `"${id}"`).join(', ')}]`);
                                    }
                                    
                                    return (
                                      <div key={borcIndex} className={`debt-item ${isSelected ? 'selected' : ''}`}>
                                        <label className="debt-checkbox">
                                          <input 
                                            type="checkbox" 
                                            checked={isSelected}
                                            onChange={(e) => handleBorcSelection(e, borc, grup)}
                                          />
                                          <span className="checkmark"></span>
                                        </label>
                                        
                                        <div className="debt-details">
                                          <div className="debt-description">{borc.ACIKLAMA}</div>
                                          <div className="debt-meta">
                                            <span className="debt-date">📅 {turkceAy(borc.TARIH)}</span>
                                            <span className="debt-amount">{formatCurrency(borcTutar)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sağ: Sepet */}
                <div className="cart-section">
                  <div className="section-card">
                    <div className="section-header">
                      <h3>🛒 Sepetim</h3>
                      {sepetItems.length > 0 && (
                        <button className="clear-cart-btn" onClick={clearCart}>
                          🗑️ Temizle
                        </button>
                      )}
                    </div>
                    
                    <div className="cart-content">
                      {sepetItems.length === 0 ? (
                        <div className="empty-cart">
                          <div className="empty-cart-icon">🛒</div>
                          <p>Sepetiniz boş</p>
                          <small>Ödemek istediğiniz borçları seçin</small>
                        </div>
                      ) : (
                        <>
                          <div className="cart-items">
                            {sepetItems.map((item, index) => (
                              <div key={index} className="cart-item">
                                <div className="cart-item-info">
                                  <div className="cart-item-title">{item.AdSoyad}</div>
                                  <div className="cart-item-desc">{item.Aciklama}</div>
                                </div>
                                <div className="cart-item-amount">
                                  {formatCurrency(parseFloat(item.Tutar.toString()))}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <div className="cart-summary">
                            <div className="cart-total">
                              <span>Toplam Tutar</span>
                              <strong>{formatCurrency(toplamTutar)}</strong>
                            </div>
                            
                            <button className="payment-proceed-btn" onClick={goToPayment}>
                              💳 Ödemeye Geç
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Ödeme Adımı */}
          {activeStep === 'payment' && (
            <div className="step-content">
              <div className="payment-layout">
                
                {/* Sol: Ödeme Özeti */}
                <div className="payment-summary-section">
                  <div className="section-card">
                    <div className="section-header">
                      <h3>📋 Ödeme Özeti</h3>
                    </div>
                    
                    <div className="payment-summary-content">
                      <div className="summary-items">
                        {sepetItems.map((item, index) => (
                          <div key={index} className="summary-item">
                            <div className="summary-info">
                              <div className="summary-title">{item.AdSoyad}</div>
                              <div className="summary-desc">{item.Aciklama}</div>
                            </div>
                            <div className="summary-amount">
                              {formatCurrency(parseFloat(item.Tutar.toString()))}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="summary-total">
                        <div className="total-row">
                          <span>Toplam Tutar</span>
                          <strong className="total-amount">{formatCurrency(toplamTutar)}</strong>
                        </div>
                      </div>
                      
                      <button 
                        className="back-to-debts-btn"
                        onClick={() => setActiveStep('debts')}
                      >
                        ← Borçlara Dön
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sağ: Ödeme Formu */}
                <div className="payment-form-section">
                  <div className="section-card">
                    <div className="section-header">
                      <h3>💳 Kart Bilgileri</h3>
                      <div className="secure-badge">🔒 Güvenli</div>
                    </div>
                    
                    <form onSubmit={handlePaymentSubmit} className="modern-payment-form">
                      <div className="form-group">
                        <label>Kart Sahibinin Adı</label>
                        <input 
                          type="text" 
                          value={kartBilgileri.cardName}
                          onChange={(e) => setKartBilgileri(prev => ({...prev, cardName: e.target.value.toUpperCase()}))}
                          placeholder="AD SOYAD"
                          required 
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Kart Numarası</label>
                        <input 
                          type="text" 
                          value={kartBilgileri.cardNumber}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\s/g, '').replace(/[^0-9]/gi, '');
                            let formattedValue = value.match(/.{1,4}/g)?.join(' ') || '';
                            if (formattedValue.length <= 19) {
                              setKartBilgileri(prev => ({...prev, cardNumber: formattedValue}));
                            }
                          }}
                          placeholder="1234 5678 9012 3456"
                          maxLength={19}
                          required 
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Son Kullanma Tarihi</label>
                          <input 
                            type="text" 
                            value={kartBilgileri.cardExpiry}
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, '');
                              if (value.length >= 2) {
                                value = value.substring(0,2) + '/' + value.substring(2,4);
                              }
                              setKartBilgileri(prev => ({...prev, cardExpiry: value}));
                            }}
                            placeholder="MM/YY"
                            maxLength={5}
                            required 
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>CVV</label>
                          <input 
                            type="password" 
                            value={kartBilgileri.cardCvv}
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 3) {
                                setKartBilgileri(prev => ({...prev, cardCvv: value}));
                              }
                            }}
                            placeholder="123"
                            maxLength={3}
                            required 
                          />
                        </div>
                      </div>
                      
                      <button 
                        type="submit" 
                        className="payment-submit-btn"
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <div className="processing-spinner"></div>
                            İşleniyor...
                          </>
                        ) : (
                          <>
                            🔒 {formatCurrency(toplamTutar)} Öde
                          </>
                        )}
                      </button>
                      
                      <div className="payment-security">
                        <small>🛡️ Ödemeniz SSL ile korunmaktadır</small>
                      </div>
                    </form>
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>
      <TabBar />
    </div>
  );
};

export default PaymentSimple; 